/**
 * MemberPortalSections — views into existing records for one member.
 * No copies: tasks/goals/schedule/profile all read and write the central
 * HomeOSState records via handlers passed from MemberPortalPage.
 */
import { Caption, TextLink } from "./os/editorial";
import { QuietCheck, fmtClock } from "./TodayBriefSections";

const rowText = { color: "var(--ara-ink)", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 500 };
const softText = { color: "var(--ara-ink-soft)" };

function EmptyLine({ children, testid }) {
  return (
    <p className="prose-ara text-[14.5px] italic" style={softText} data-testid={testid}>
      {children}
    </p>
  );
}

function EventRow({ event, last }) {
  return (
    <div
      className={`flex items-baseline gap-4 py-3 ${last ? "" : "border-b"}`}
      style={{ borderColor: "var(--ara-line-soft)" }}
      data-testid={`portal-event-${event.id}`}
    >
      <p className="w-[72px] flex-shrink-0 text-[13px] tabular-nums" style={{ ...softText, fontFamily: "Inter, system-ui, sans-serif" }}>
        {fmtClock(event.startTime)}
      </p>
      <div className="min-w-0">
        <p className="text-[14.5px] leading-snug" style={rowText}>
          {event.title}{event.shared ? <span style={{ ...softText, fontWeight: 400 }}> · whole family</span> : null}
        </p>
        {event.location && <p className="mt-0.5 caption-ara" style={softText}>{event.location}</p>}
      </div>
    </div>
  );
}

function TaskRow({ task, onComplete, last }) {
  return (
    <div
      className={`flex items-start gap-4 py-3 ${last ? "" : "border-b"}`}
      style={{ borderColor: "var(--ara-line-soft)" }}
      data-testid={`portal-task-${task.id}`}
    >
      <QuietCheck onCheck={() => onComplete(task)} testid={`portal-task-check-${task.id}`} label={`Mark ${task.title} done`} />
      <div className="min-w-0">
        <p className="text-[14.5px] leading-snug" style={rowText}>{task.title}</p>
        {task.dueLabel && <p className="mt-0.5 caption-ara" style={softText}>{task.dueLabel}</p>}
      </div>
    </div>
  );
}

/* ── 1 · Today ─────────────────────────────────────────────────────────── */
export function PortalToday({ events, openTasks, worthKnowing, onComplete }) {
  return (
    <div data-testid="portal-today">
      <Caption>Next today</Caption>
      <div className="mt-2 mb-8">
        {events.length === 0
          ? <EmptyLine testid="portal-today-no-events">Nothing on the schedule today.</EmptyLine>
          : events.map((e, i) => <EventRow key={e.id} event={e} last={i === events.length - 1} />)}
      </div>
      <Caption>To do</Caption>
      <div className="mt-2">
        {openTasks.length === 0
          ? <EmptyLine testid="portal-today-no-tasks">Nothing waiting. Enjoy the day.</EmptyLine>
          : openTasks.map((t, i) => <TaskRow key={t.id} task={t} onComplete={onComplete} last={i === openTasks.length - 1} />)}
      </div>
      {worthKnowing && (
        <p className="mt-8 prose-ara text-[14px] italic" style={softText} data-testid="portal-today-note">
          Worth knowing — {worthKnowing}
        </p>
      )}
    </div>
  );
}

/* ── 2 · Calendar ──────────────────────────────────────────────────────── */
export function PortalCalendar({ events, week, birthdayLine }) {
  return (
    <div data-testid="portal-calendar">
      {birthdayLine && (
        <p className="mb-6 prose-ara text-[14px] italic" style={softText} data-testid="portal-calendar-birthday">
          {birthdayLine}
        </p>
      )}
      <Caption>Today's schedule</Caption>
      <div className="mt-2 mb-8">
        {events.length === 0
          ? <EmptyLine testid="portal-calendar-no-events">A quiet day — nothing timed.</EmptyLine>
          : events.map((e, i) => <EventRow key={e.id} event={e} last={i === events.length - 1} />)}
      </div>
      <Caption>This week</Caption>
      <div className="mt-2" data-testid="portal-calendar-week">
        {week.every(d => d.items.length === 0)
          ? <EmptyLine testid="portal-calendar-empty-week">Nothing dated this week.</EmptyLine>
          : week.filter(d => d.items.length > 0).map(d => (
            <div key={d.iso} className="py-3 border-b last:border-b-0" style={{ borderColor: "var(--ara-line-soft)" }} data-testid={`portal-week-${d.iso}`}>
              <p className="caption-ara" style={softText}>{d.label}</p>
              {d.items.map(t => (
                <p key={t.id} className="mt-1.5 text-[14.5px] leading-snug" style={rowText}>
                  {t.title}{t.dueTime ? <span style={{ ...softText, fontWeight: 400 }}> · {fmtClock(t.dueTime)}</span> : null}
                </p>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}

/* ── 4 · Goals — central state.goals, edits update the same record ─────── */
const GOAL_STATUS = { not_started: "Not started", in_progress: "In progress", complete: "Complete" };

export function PortalGoals({ goals, editingId, draft, setDraft, onStartEdit, onSave, onCancel, onAdd, onRemove }) {
  return (
    <div data-testid="portal-goals">
      {goals.length === 0 && <EmptyLine testid="portal-goals-empty">No personal goals recorded yet.</EmptyLine>}
      {goals.map((g, i) => (
        <div key={g.id} className={`py-4 ${i === goals.length - 1 ? "" : "border-b"}`} style={{ borderColor: "var(--ara-line-soft)" }} data-testid={`portal-goal-${g.id}`}>
          {editingId === g.id ? (
            <div className="space-y-3" data-testid={`portal-goal-edit-${g.id}`}>
              <input
                className="w-full bg-transparent border-b pb-1 text-[15px] outline-none"
                style={{ borderColor: "var(--ara-line)", color: "var(--ara-ink)" }}
                value={draft.title}
                onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                placeholder="Goal"
                data-testid="portal-goal-title-input"
              />
              <input
                className="w-full bg-transparent border-b pb-1 text-[14px] outline-none"
                style={{ borderColor: "var(--ara-line)", color: "var(--ara-ink)" }}
                value={draft.progress}
                onChange={e => setDraft(d => ({ ...d, progress: e.target.value }))}
                placeholder="Progress or next action"
                data-testid="portal-goal-progress-input"
              />
              <div className="flex items-center gap-5">
                <select
                  className="bg-transparent text-[13px] outline-none"
                  style={{ color: "var(--ara-ink)" }}
                  value={draft.status}
                  onChange={e => setDraft(d => ({ ...d, status: e.target.value }))}
                  data-testid="portal-goal-status-select"
                >
                  {Object.entries(GOAL_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <TextLink onClick={onSave} testid="portal-goal-save">Save →</TextLink>
                <TextLink onClick={onCancel}>Cancel</TextLink>
                <TextLink onClick={() => onRemove(g.id)} testid={`portal-goal-remove-${g.id}`}>Remove</TextLink>
              </div>
            </div>
          ) : (
            <div className="flex items-baseline justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[15.5px] leading-snug" style={rowText}>{g.title || "Untitled goal"}</p>
                <p className="mt-1 caption-ara" style={softText}>
                  {[GOAL_STATUS[g.status] || "Not started", g.category, g.progress].filter(Boolean).join(" · ")}
                </p>
              </div>
              <TextLink onClick={() => onStartEdit(g)} testid={`portal-goal-edit-link-${g.id}`}>Edit</TextLink>
            </div>
          )}
        </div>
      ))}
      <div className="mt-5">
        <TextLink onClick={onAdd} testid="portal-goal-add">Add a goal →</TextLink>
      </div>
    </div>
  );
}

/* ── 5 · Planning — existing dated commitments + profile rhythm ────────── */
export function PortalPlanning({ upcoming, responsibilities, rhythmRows, onEditProfile }) {
  return (
    <div data-testid="portal-planning">
      <Caption>Coming up</Caption>
      <div className="mt-2 mb-8">
        {upcoming.length === 0
          ? <EmptyLine testid="portal-planning-empty">Nothing dated on the horizon.</EmptyLine>
          : upcoming.map((t, i) => (
            <div key={t.id} className={`flex items-baseline gap-4 py-3 ${i === upcoming.length - 1 ? "" : "border-b"}`} style={{ borderColor: "var(--ara-line-soft)" }}>
              <p className="w-[88px] flex-shrink-0 caption-ara" style={softText}>{t.whenLabel}</p>
              <p className="text-[14.5px] leading-snug" style={rowText}>{t.title}</p>
            </div>
          ))}
      </div>
      <Caption>Responsibilities</Caption>
      <div className="mt-2 mb-8" data-testid="portal-planning-responsibilities">
        {responsibilities.length === 0
          ? <EmptyLine>None recorded on the profile.</EmptyLine>
          : <p className="text-[14.5px] leading-relaxed" style={rowText}>{responsibilities.join(" · ")}</p>}
      </div>
      <Caption>Rhythm</Caption>
      <div className="mt-2" data-testid="portal-planning-rhythm">
        {rhythmRows.length === 0
          ? <EmptyLine>No rhythm noted yet.</EmptyLine>
          : rhythmRows.map(([label, value]) => (
            <div key={label} className="py-2.5 border-b last:border-b-0 grid grid-cols-[140px_1fr] gap-4" style={{ borderColor: "var(--ara-line-soft)" }}>
              <p className="caption-ara" style={softText}>{label}</p>
              <p className="text-[14px] leading-relaxed" style={rowText}>{value}</p>
            </div>
          ))}
      </div>
      <div className="mt-6">
        <TextLink onClick={onEditProfile} testid="portal-planning-edit-profile">Edit on the profile →</TextLink>
      </div>
    </div>
  );
}

/* ── 6 · Money — owner/partner only in Phase 1 ─────────────────────────── */
export function PortalMoney({ allowed, savings, onOpenStudy }) {
  if (!allowed) {
    return (
      <div data-testid="portal-money-restricted">
        <EmptyLine>Money inside a member's space is available to household owners and partners only.</EmptyLine>
      </div>
    );
  }
  return (
    <div data-testid="portal-money">
      {savings.length === 0 ? (
        <div data-testid="portal-money-empty">
          <p
            className="italic text-[17px] leading-[1.45]"
            style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink)", fontWeight: 400, maxWidth: "36ch" }}
          >
            Nothing personal has been set up here yet.
          </p>
          <p className="mt-3 prose-ara text-[14px]" style={softText}>
            Household money lives in the Study. Personal savings goals recorded there under this name will appear here.
          </p>
        </div>
      ) : (
        <>
          <Caption>Savings goals</Caption>
          <div className="mt-2">
            {savings.map((g, i) => (
              <div key={g.id} className={`flex items-baseline justify-between gap-4 py-3 ${i === savings.length - 1 ? "" : "border-b"}`} style={{ borderColor: "var(--ara-line-soft)" }} data-testid={`portal-money-goal-${g.id}`}>
                <p className="text-[14.5px]" style={rowText}>{g.name}</p>
                <p className="caption-ara" style={softText}>{[g.current, g.target].filter(Boolean).join(" of ")}</p>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="mt-6">
        <TextLink onClick={onOpenStudy} testid="portal-money-open-study">Open the Study →</TextLink>
      </div>
    </div>
  );
}

/* ── 7 · Chores — central tasks, checkbox completes the same record ────── */
export function PortalChores({ groups, onComplete }) {
  const total = groups.reduce((n, g) => n + g.chores.length, 0);
  return (
    <div data-testid="portal-chores">
      {total === 0 && <EmptyLine testid="portal-chores-empty">No chores on the chart this week.</EmptyLine>}
      {groups.filter(g => g.chores.length > 0).map((g, gi) => (
        <div key={g.key} className={gi === 0 ? "" : "mt-6"} data-testid={`portal-chores-${g.key}`}>
          <Caption>{g.label}</Caption>
          <div className="mt-2">
            {g.chores.map((t, i) => <TaskRow key={t.id} task={t} onComplete={onComplete} last={i === g.chores.length - 1} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
